import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SatelliteDataService {
  private urlConst = environment.url.HEATWAVE_STAGING_API_URL;

  /** Sorted unique date keys (yyyyMMdd) extracted from API response */
  allDates: string[] = [];

  /** Timestamps grouped and sorted per date key */
  groupedTimestamps: { [dateKey: string]: string[] } = {};

  /** Currently active date key (yyyyMMdd) */
  selectedDateKey: string = null;

  constructor(private http: HttpClient) { }
  getHeirarchy(postData: any): Observable<any> {
    const url = this.urlConst + 'locations/heirarchy';
    return this.http.post<any>(url, postData);
  }

  getCTPTimestamps(): Observable<string[]> {
    const url = environment.url.HEATWAVE_STAGING_API_URL
      + 'surfaceTemp/layerTimeDimensionsV2/cloud_top_temperature/ctp_latest/cloud_top_temperature';
    return this.http.get<string[]>(url);
  }

  /**
   * WMS GetFeatureInfo on the CTT raster for pixel (x, y) of an EPSG:3857 image covering `bbox`.
   * Uses the same time CQL filter as the rendered tiles so the value matches the visible frame.
   * Emits the GRAY_INDEX value, or null when the server returns no feature (outside the raster).
   */
  getCTTGrayIndex(layerName: string, timestamp: string, bbox: string,
                  width: number, height: number, x: number, y: number): Observable<number> {
    const url = environment.url.COMMON_GEOSERVER_URL + layerName.split(':')[0] + '/wms';
    const params = new HttpParams({
      fromObject: {
        SERVICE: 'WMS',
        VERSION: '1.1.1',
        REQUEST: 'GetFeatureInfo',
        LAYERS: layerName,
        QUERY_LAYERS: layerName,
        STYLES: '',
        SRS: 'EPSG:3857',
        BBOX: bbox,
        WIDTH: String(width),
        HEIGHT: String(height),
        X: String(x),
        Y: String(y),
        INFO_FORMAT: 'application/json',
        FEATURE_COUNT: '1',
        CQL_FILTER: `time='${timestamp}'`
      }
    });
    return this.http.get<any>(url, { params }).pipe(map(res => this.parseGrayIndex(res)));
  }

  /** Extracts GRAY_INDEX from a GetFeatureInfo GeoJSON response; null if absent or non-numeric. */
  parseGrayIndex(res: any): number {
    const feature = res && Array.isArray(res.features) ? res.features[0] : null;
    const value = feature && feature.properties ? feature.properties.GRAY_INDEX : null;
    return typeof value === 'number' && isFinite(value) ? value : null;
  }

  /** Display text for a GRAY_INDEX value (raster values are in °C, matching the legend). */
  formatGrayIndex(value: number): string {
    return value.toFixed(2) + ' °C';
  }

  /** Group a flat timestamp list by date, sort each group, populate allDates */
  buildGroupedData(rawList: string[]) {
    const grouped: { [dateKey: string]: string[] } = {};
    for (const ts of rawList) {
      const dateKey = ts.substring(0, 8); // yyyyMMdd
      if (!grouped[dateKey]) { grouped[dateKey] = []; }
      grouped[dateKey].push(ts);
    }
    for (const key in grouped) { grouped[key].sort(); }
    this.groupedTimestamps = grouped;
    this.allDates = Object.keys(grouped).sort();
  }

  /** Return today's date key if present, otherwise the most recent available */
  getActiveDate(): string {
    if (this.allDates.length === 0) { return null; }
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const todayKey = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    return this.allDates.includes(todayKey)
      ? todayKey
      : this.allDates[this.allDates.length - 1];
  }

  /** Format a full timestamp (yyyyMMddHHmm) → 'dd/MM/yyyy HH:mm' */
  formatTimestamp(ts: string): string {
    return `${ts.substring(6, 8)}/${ts.substring(4, 6)}/${ts.substring(0, 4)} ${ts.substring(8, 10)}:${ts.substring(10, 12)}`;
  }

  /** Format a date key (yyyyMMdd) → 'dd/MM/yyyy' */
  formatDateKey(dateKey: string): string {
    return `${dateKey.substring(6, 8)}/${dateKey.substring(4, 6)}/${dateKey.substring(0, 4)}`;
  }
}
